import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
    ChartBar, Users, EnvelopeOpen, FileText, PaperPlaneTilt,
    SignOut, House, Lightning, UserCircle, MagnifyingGlass,
    List, X, DownloadSimple, ArrowClockwise, DeviceMobile, Heartbeat,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { useAdminPWA } from "@/lib/useAdminPWA";
import { toast } from "sonner";

const NAV = [
    { to: "/admin", label: "Overview", icon: ChartBar, end: true },
    { to: "/admin/leads", label: "Website Leads", icon: PaperPlaneTilt },
    { to: "/admin/lead-finder", label: "Lead Finder", icon: MagnifyingGlass, badge: "New" },
    { to: "/admin/contacts", label: "Contacts", icon: Users },
    { to: "/admin/templates", label: "Templates", icon: FileText },
    { to: "/admin/campaigns", label: "Campaigns", icon: EnvelopeOpen },
    { to: "/admin/activity", label: "Activity Feed", icon: Lightning },
    { to: "/admin/system-health", label: "System Health", icon: Heartbeat },
];

export default function AdminLayout({ children }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const {
        canInstall, isStandalone, promptInstall, updateAvailable, applyUpdate,
    } = useAdminPWA();

    // Auto-close mobile drawer when route changes.
    useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

    // Lock body scroll when drawer is open (prevents scroll-bleed on iOS).
    useEffect(() => {
        if (mobileOpen) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [mobileOpen]);

    const handleLogout = async () => {
        await logout();
        nav("/admin/login");
    };

    const handleInstall = async () => {
        const res = await promptInstall();
        if (res?.outcome === "accepted") {
            toast.success("Admin panel installed — launch it from your home screen.");
        }
    };

    // Derive current page label for mobile header.
    const currentNav = NAV.find(
        (n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to))
    );

    return (
        <div
            className="min-h-screen flex bg-[#030712]"
            data-testid="admin-layout"
            style={{
                paddingLeft: "env(safe-area-inset-left)",
                paddingRight: "env(safe-area-inset-right)",
                paddingBottom: "env(safe-area-inset-bottom)",
            }}
        >
            {/* --- MOBILE TOP BAR (< lg) — safe-area aware for iPhone notch --- */}
            <header
                className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#050912]/95 backdrop-blur-md border-b border-slate-800 flex items-center gap-3 px-4"
                data-testid="admin-mobile-header"
                style={{
                    paddingTop: "env(safe-area-inset-top)",
                    paddingLeft: "max(1rem, env(safe-area-inset-left))",
                    paddingRight: "max(1rem, env(safe-area-inset-right))",
                    minHeight: "calc(3.5rem + env(safe-area-inset-top))",
                }}
            >
                <button
                    onClick={() => setMobileOpen(true)}
                    className="p-2.5 -ml-1 rounded-lg text-slate-300 hover:bg-slate-800 active:bg-slate-700 min-w-[44px] min-h-[44px] flex items-center justify-center"
                    data-testid="admin-mobile-menu-btn"
                    aria-label="Open navigation"
                >
                    <List size={22} weight="bold" />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] overline !text-orange-400 leading-none">Admin</div>
                    <div className="font-heading text-sm font-semibold text-slate-100 truncate leading-tight">
                        {currentNav?.label || "Surveys2CAD"}
                    </div>
                </div>
                {updateAvailable && (
                    <button
                        onClick={applyUpdate}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 min-h-[40px]"
                        data-testid="admin-update-btn-mobile"
                    >
                        <ArrowClockwise size={12} weight="bold" /> Update
                    </button>
                )}
                {canInstall && !isStandalone && (
                    <button
                        onClick={handleInstall}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] bg-orange-500/20 border border-orange-400/40 text-orange-200 min-h-[40px]"
                        data-testid="admin-install-btn-mobile"
                    >
                        <DownloadSimple size={12} weight="bold" /> Install
                    </button>
                )}
            </header>

            {/* --- MOBILE DRAWER BACKDROP --- */}
            {mobileOpen && (
                <div
                    onClick={() => setMobileOpen(false)}
                    className="lg:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                    data-testid="admin-drawer-backdrop"
                />
            )}

            {/* --- SIDEBAR (desktop static; mobile drawer) --- */}
            <aside
                className={`fixed lg:sticky top-0 left-0 z-[51] lg:z-auto w-72 sm:w-80 lg:w-64 h-[100dvh] lg:h-screen
                    border-r border-slate-800 bg-[#050912] flex flex-col flex-shrink-0
                    transition-transform duration-300 ease-out lg:transition-none
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
                data-testid="admin-sidebar"
                aria-label="Admin navigation"
                style={{
                    paddingTop: "env(safe-area-inset-top)",
                    paddingBottom: "env(safe-area-inset-bottom)",
                }}
            >
                <div className="p-5 border-b border-slate-800 flex items-start justify-between gap-3">
                    <div>
                        <Logo size="sm" />
                        <div className="overline mt-3 !text-orange-400">Admin Panel</div>
                    </div>
                    <button
                        onClick={() => setMobileOpen(false)}
                        className="lg:hidden p-2.5 rounded-lg text-slate-400 hover:bg-slate-800 active:bg-slate-700 min-w-[44px] min-h-[44px] flex items-center justify-center"
                        aria-label="Close navigation"
                        data-testid="admin-drawer-close-btn"
                    >
                        <X size={20} />
                    </button>
                </div>

                <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="admin-nav">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-3 lg:py-2.5 rounded-lg text-sm font-heading font-medium transition-colors ${
                                    isActive
                                        ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
                                        : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/40 border border-transparent"
                                }`
                            }
                        >
                            <n.icon size={18} weight="duotone" />
                            <span className="flex-1">{n.label}</span>
                            {n.badge && (
                                <span className="px-1.5 py-0.5 text-[9px] font-mono-alt rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/40">
                                    {n.badge}
                                </span>
                            )}
                        </NavLink>
                    ))}

                    {/* PWA install / update / installed CTA — extracted to a sub-component
                        so the sidebar stays free of nested ternaries. */}
                    <PwaCta
                        canInstall={canInstall}
                        isStandalone={isStandalone}
                        updateAvailable={updateAvailable}
                        onInstall={handleInstall}
                        onUpdate={applyUpdate}
                    />
                </nav>

                <div className="border-t border-slate-800 p-4">
                    <div className="tech-card p-3 mb-3">
                        <div className="flex items-center gap-2">
                            <UserCircle size={22} weight="duotone" className="text-cyan-300" />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-heading font-semibold text-slate-100 truncate">{user?.name || "Admin"}</div>
                                <div className="text-[10px] text-slate-500 truncate font-mono-alt">{user?.email}</div>
                            </div>
                        </div>
                    </div>
                    <Link to="/" className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-cyan-300 rounded-lg">
                        <House size={14} /> Back to site
                    </Link>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-red-300 rounded-lg"
                        data-testid="admin-logout-btn"
                    >
                        <SignOut size={14} /> Sign out
                    </button>
                </div>
            </aside>

            {/* --- MAIN CONTENT --- */}
            <div className="flex-1 min-w-0 flex flex-col w-full">
                <main
                    className="flex-1 overflow-x-hidden"
                    style={{
                        paddingTop: "calc(3.5rem + env(safe-area-inset-top))",
                    }}
                    data-admin-main
                >
                    <div className="max-w-[1400px] mx-auto w-full lg:pt-0"
                        style={{ paddingTop: 0 }}>
                        <style>{`
                            @media (min-width: 1024px) {
                                [data-admin-main] { padding-top: 0 !important; }
                            }
                        `}</style>
                        {children}
                    </div>
                </main>
            </div>
            <Toaster position="top-right" theme="dark" />
        </div>
    );
}


/** Sidebar CTA — renders whichever of the 3 PWA states is active.
 *  Split out to keep the parent free of nested ternaries and under complexity thresholds. */
function PwaCta({ canInstall, isStandalone, updateAvailable, onInstall, onUpdate }) {
    if (!canInstall && !isStandalone && !updateAvailable) return null;

    let body;
    if (updateAvailable) {
        body = (
            <button
                onClick={onUpdate}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-cyan-200 bg-cyan-500/10 border border-cyan-500/40 hover:bg-cyan-500/20"
                data-testid="admin-update-btn"
            >
                <ArrowClockwise size={16} weight="bold" />
                <span className="flex-1 text-left font-heading">Update available</span>
            </button>
        );
    } else if (isStandalone) {
        body = (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-emerald-300" data-testid="admin-installed-badge">
                <DeviceMobile size={14} weight="fill" />
                <span>Installed · Running as app</span>
            </div>
        );
    } else {
        body = (
            <button
                onClick={onInstall}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-orange-200 bg-orange-500/10 border border-orange-500/40 hover:bg-orange-500/20"
                data-testid="admin-install-btn"
            >
                <DownloadSimple size={16} weight="bold" />
                <span className="flex-1 text-left font-heading">Install as App</span>
            </button>
        );
    }
    return <div className="pt-3 mt-3 border-t border-slate-800/60">{body}</div>;
}
